/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2026-06-13
 */

// Add third-party dependencies.
import { Box, Typography, Chip, Collapse } from '@mui/material';
import { useState } from 'react';

// Add local dependencies.
import type { ChangelogEntry as ChangelogEntryType } from '../../../types/changelog';
import ChangelogEntry from './ChangelogEntry';

/**
 * Props for the ChangelogYearSection component.
 */
interface ChangelogYearSectionProps {
  /** Four-digit year label (e.g., "2026") */
  year: string;
  /** Entries released in this year, newest first */
  entries: ChangelogEntryType[];
  /** Whether the section starts expanded (current year is open by default) */
  defaultExpanded?: boolean;
  /** Version string of the single newest published entry, for the "Latest" badge */
  latestVersion?: string | null;
}

/**
 * A collapsible section grouping all changelog entries from one calendar year.
 *
 * The current year renders expanded; older years collapse behind a clickable
 * header so the page stays scannable as the changelog grows. Per-entry
 * expand/collapse still works independently inside an open year.
 *
 * @param props - The component props
 * @returns {React.ReactElement} The year section component
 */
const ChangelogYearSection: React.FC<ChangelogYearSectionProps> = ({
  year,
  entries,
  defaultExpanded = false,
  latestVersion,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const handleToggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <Box sx={{ mb: 4 }}>
      {/* Year header (click to expand/collapse the whole year) */}
      <Box
        onClick={handleToggleExpanded}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          py: 1.5,
          mb: 3,
          borderBottom: 2,
          borderColor: 'divider',
          transition: 'border-color 0.2s ease-in-out',
          '&:hover': {
            borderColor: 'primary.main',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 700 }}>
            {year}
          </Typography>
          <Chip
            label={`${entries.length} version${entries.length !== 1 ? 's' : ''}`}
            size="small"
            variant="outlined"
          />
        </Box>
        <Typography variant="body2" color="primary">
          {isExpanded ? 'Click to collapse' : 'Click to expand'}
        </Typography>
      </Box>

      {/* Entries for this year */}
      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <Box>
          {entries.map(entry => (
            <ChangelogEntry
              key={entry.version}
              entry={entry}
              isLatest={!!latestVersion && entry.version === latestVersion}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

export default ChangelogYearSection;
